import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage1.css";
import { fireGameAlert } from "./gameAlert";
import {
  buildWordSearchGrid,
  cellKey,
  getCellsOnPath,
} from "./wordSearchUtils";
import stage1Bg from "./img/stage1bg.png";
import title1Img from "./img/title1.png";
import officeImg from "./img/office.png";
import garlandsImg from "./img/Garlands.png";
import dandiyaImg from "./img/Dandiya.png";
import kalashImg from "./img/Kalash.png";
import drapesImg from "./img/Drapes.png";
import puppetsImg from "./img/Puppets.png";
import umbrellaImg from "./img/Umbrella.png";
import rangoliImg from "./img/Rangoli.png";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 1;
/** Minimum words the player must find before they can submit / advance. */
const MIN_WORDS_TO_ADVANCE = 5;
const GRID_SIZE = 13;

const STAGE1_DECORS = [
  { word: "GARLANDS", overlayClass: "stage1-child--garlands", src: garlandsImg },
  { word: "DANDIYA", overlayClass: "stage1-child--dandiya", src: dandiyaImg },
  { word: "KALASH", overlayClass: "stage1-child--kalash", src: kalashImg },
  { word: "DRAPES", overlayClass: "stage1-child--drapes", src: drapesImg },
  { word: "PUPPETS", overlayClass: "stage1-child--puppets", src: puppetsImg },
  { word: "UMBRELLA", overlayClass: "stage1-child--umbrella", src: umbrellaImg },
  { word: "RANGOLI", overlayClass: "stage1-child--rangoli", src: rangoliImg },
];

const STATIC_WORDS = STAGE1_DECORS.map((item) => item.word);

const FOUND_HIGHLIGHTS = [
  "rgba(245, 166, 35, 0.75)",
  "rgba(91, 192, 222, 0.75)",
  "rgba(155, 89, 182, 0.7)",
  "rgba(61, 187, 109, 0.75)",
  "rgba(255, 107, 90, 0.75)",
  "rgba(100, 149, 237, 0.75)",
  "rgba(255, 193, 7, 0.75)",
  "rgba(236, 112, 99, 0.75)",
];

function measureHighlightPillStyle(gridEl, wrapEl, cells) {
  if (!gridEl || !wrapEl || !cells.length) return null;

  const start = cells[0];
  const end = cells[cells.length - 1];
  const startEl = gridEl.querySelector(
    `[data-row="${start.row}"][data-col="${start.col}"]`
  );
  const endEl = gridEl.querySelector(
    `[data-row="${end.row}"][data-col="${end.col}"]`
  );
  if (!startEl || !endEl) return null;

  const wrapRect = wrapEl.getBoundingClientRect();
  const startRect = startEl.getBoundingClientRect();
  const endRect = endEl.getBoundingClientRect();

  const x0 = startRect.left + startRect.width / 2 - wrapRect.left;
  const y0 = startRect.top + startRect.height / 2 - wrapRect.top;
  const x1 = endRect.left + endRect.width / 2 - wrapRect.left;
  const y1 = endRect.top + endRect.height / 2 - wrapRect.top;

  const dx = x1 - x0;
  const dy = y1 - y0;
  const centerDistance = Math.sqrt(dx * dx + dy * dy);
  const angleRad = Math.atan2(dy, dx);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const cellSize = (startRect.width + endRect.width) / 2;
  const pillThickness = Math.max(
    16,
    Math.round(((startRect.height + endRect.height) / 2) * 0.78)
  );
  const halfCell = cellSize / 2;
  const startX = x0 - halfCell * cos;
  const startY = y0 - halfCell * sin;

  return {
    left: startX,
    top: startY - pillThickness / 2,
    width: centerDistance + cellSize,
    height: pillThickness,
    transform: `rotate(${angleRad * (180 / Math.PI)}deg)`,
    transformOrigin: "0 50%",
  };
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}



function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage1() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  /** Points earned in stage 1 this session (resumes from DB stage1 column). */
  const [stage1Points, setStage1Points] = useState(0);
  /** HUD total = other stages + stage1Points. */
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [foundWords, setFoundWords] = useState(() => new Set());
  const [activeCells, setActiveCells] = useState([]);
  const activeCellsRef = useRef([]);
  const [foundHighlights, setFoundHighlights] = useState([]);
  const [measuredPillStyles, setMeasuredPillStyles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const gridRef = useRef(null);
  const gridWrapRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startCellRef = useRef(null);
  const foundRef = useRef(new Set());
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const wordEntries = useMemo(
    () => STATIC_WORDS.map((w) => ({ word: w })),
    []
  );

  const gridSize = GRID_SIZE;

  const puzzle = useMemo(() => {
    if (!wordEntries.length) return null;
    return buildWordSearchGrid(wordEntries.map((e) => e.word), gridSize);
  }, [wordEntries, gridSize]);

  const targetWords = useMemo(
    () => new Set(wordEntries.map((e) => e.word)),
    [wordEntries]
  );

  const pointsPerWord = 10;

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    html.classList.add("stage1-lock");
    body.classList.remove("common-bg");
    body.classList.add("word-search-page");
    body.style.backgroundImage = `url("${stage1Bg}")`;
    body.style.backgroundSize = "100% 100%";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      html.classList.remove("stage1-lock");
      body.classList.remove("word-search-page");
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    foundRef.current = foundWords;
  }, [foundWords]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage1Points);
  }, [stage1Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (!puzzle) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage1 = Number(st.stage1) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1);
          setStage1Points(dbStage1);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    puzzle,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    wordEntries.length,
    pointsPerWord,
    navigate,
  ]);

  const finishStage = useCallback(async (skip = false) => {
    if (submitting || submittedRef.current) return;
    if (!skip && foundRef.current.size < MIN_WORDS_TO_ADVANCE) return;
    submittedRef.current = true;
    setSubmitting(true);

    const uid =
      storedUser.userId || storedUser.userid || storedUser.id;
    const sessionId = storedUser.sessionId;
    const organizationId = storedUser.organizationId;
    const timeStr = formatSecondsToClock(elapsedRef.current);
    const stage1Score = skip ? 0 : stage1Points;
    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
    const finalTotal = otherStagesTotalRef.current + stage1Score;

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      const saveResult = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: STAGE_NUM,
        score: stage1Score,
        time: timeStr,
        total_score: finalTotal,
      });
      if (saveResult.total_score != null) {
        setDisplayTotalScore(saveResult.total_score);
      }
    }

    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);

    if (isDemoBypass) {
      window.location.href =
        String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    navigate("/stage2");
  }, [
    submitting,
    stage1Points,
    storedUser,
    isDemoBypass,
    backendBase,
    adminToken,
    stageRowId,
    navigate,
  ]);

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const markWordFound = useCallback(
    (word, cells) => {
      if (foundRef.current.has(word)) return;
      const isFirstWordCompletion = foundRef.current.size === 0;
      const colorIdx = foundRef.current.size % FOUND_HIGHLIGHTS.length;
      const color = FOUND_HIGHLIGHTS[colorIdx];

      setFoundWords((prev) => {
        const next = new Set(prev);
        next.add(word);
        return next;
      });
      foundRef.current.add(word);
      setStage1Points((p) => p + pointsPerWord);
      setFoundHighlights((prev) => [...prev, { word, cells, color }]);

      if (
        isDemoBypass &&
        isFirstWordCompletion &&
        !demoRedirectTriggeredRef.current
      ) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void fireGameAlert({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
      }
    },
    [pointsPerWord, isDemoBypass]
  );

  const resolveSelection = useCallback(
    (cells) => {
      if (!cells.length) return;
      const selected = cells
        .map(({ row, col }) => puzzle.grid[row]?.[col] || "")
        .join("");
      const reversed = [...selected].reverse().join("");

      let matched = null;
      if (targetWords.has(selected)) matched = selected;
      else if (targetWords.has(reversed)) matched = reversed;

      if (matched && !foundRef.current.has(matched)) {
        markWordFound(matched, cells);
      }
    },
    [puzzle, targetWords, markWordFound]
  );

  const updateActivePath = useCallback((endRow, endCol) => {
    const start = startCellRef.current;
    if (!start) return;
    const cells = getCellsOnPath(start, { row: endRow, col: endCol });
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerDown = useCallback((row, col) => {
    isDraggingRef.current = true;
    startCellRef.current = { row, col };
    const cells = [{ row, col }];
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerEnter = useCallback(
    (row, col) => {
      if (!isDraggingRef.current) return;
      updateActivePath(row, col);
    },
    [updateActivePath]
  );

  const onPointerUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    resolveSelection(activeCellsRef.current);
    activeCellsRef.current = [];
    setActiveCells([]);
    startCellRef.current = null;
  }, [resolveSelection]);

  const onGridTouchMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const cell = el?.closest?.("[data-row][data-col]");
      if (cell?.dataset?.row != null) {
        onPointerEnter(Number(cell.dataset.row), Number(cell.dataset.col));
      }
    },
    [onPointerEnter]
  );

  useEffect(() => {
    const up = () => onPointerUp();
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [onPointerUp]);

  const fitPuzzleGrid = useCallback(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return;

    const portraitPhone = window.matchMedia(
      "(max-width: 768px) and (orientation: portrait)"
    ).matches;
    if (portraitPhone) {
      wrap.style.removeProperty("--puzzle-size");
      wrap.classList.remove("is-fitted");
      return;
    }

    const size = Math.max(
      0,
      Math.floor(Math.min(wrap.clientWidth, wrap.clientHeight, 420))
    );
    if (size <= 0) return;

    const next = `${size}px`;
    if (wrap.style.getPropertyValue("--puzzle-size") !== next) {
      wrap.style.setProperty("--puzzle-size", next);
    }
    wrap.classList.add("is-fitted");
  }, []);

  const remeasureHighlights = useCallback(() => {
    const grid = gridRef.current;
    const wrap = gridWrapRef.current;
    if (!grid || !wrap || !foundHighlights.length) {
      setMeasuredPillStyles((prev) =>
        Object.keys(prev).length ? {} : prev
      );
      return;
    }

    const next = {};
    foundHighlights.forEach(({ word, cells, color }) => {
      const pill = measureHighlightPillStyle(grid, wrap, cells);
      if (pill) next[word] = { ...pill, background: color };
    });
    setMeasuredPillStyles(next);
  }, [foundHighlights]);

  const layoutPuzzle = useCallback(() => {
    fitPuzzleGrid();
    remeasureHighlights();
  }, [fitPuzzleGrid, remeasureHighlights]);

  useLayoutEffect(() => {
    layoutPuzzle();
  }, [layoutPuzzle, gridSize]);

  useEffect(() => {
    const grid = gridRef.current;
    const wrap = gridWrapRef.current;
    if (!grid || !wrap) return;

    const observer = new ResizeObserver(layoutPuzzle);
    observer.observe(grid);
    observer.observe(wrap);
    window.addEventListener("orientationchange", layoutPuzzle);
    window.addEventListener("resize", layoutPuzzle);
    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", layoutPuzzle);
      window.removeEventListener("resize", layoutPuzzle);
    };
  }, [layoutPuzzle, puzzle, gridSize]);

  if (!dbReady) {
    return <div className="stage1-loading">Loading game…</div>;
  }

  if (!puzzle) {
    return <div className="stage1-empty">No word puzzle configured.</div>;
  }

  const activeSet = new Set(activeCells.map((c) => cellKey(c.row, c.col)));
  const clock = formatSecondsToClock(elapsedSec);

  return (
    <div className="stage1-page">
      <div className="stage1-header">
        <img src={title1Img} alt="Eco-Friendly Decor" className="stage1-title-img" />
        <p className="stage1-instructions">
          Your first task is to use sustainable Navratri decorations to design
          your virtual workstation. Pick wisely from the options and decorate to
          your best. Clock up to share it with all.
          <br />
          Drag to select the items.
        </p>
      </div>

      <div className="stage1-hud">
        <div className="stage1-chip" aria-label={`Time ${clock}`}>
          Time: {clock}
        </div>
        <div className="stage1-hud__right">
          <div className="stage1-chip" aria-label={`Points ${displayTotalScore}`}>
            Points: {displayTotalScore}
          </div>
          <button
            type="button"
            className="stage1-skip"
            disabled={submitting}
            onClick={handleSkip}
          >
            Skip
          </button>
        </div>
      </div>

      <div className="stage1-main">
        <div className="stage1-left">
          <div className="stage1-grid-wrap" ref={gridWrapRef}>
            <div className="stage1-highlights" aria-hidden>
              {foundHighlights.map(({ word }) => {
                const pillStyle = measuredPillStyles[word];
                if (!pillStyle) return null;
                return (
                  <div
                    key={word}
                    className="stage1-highlight-pill"
                    style={pillStyle}
                  />
                );
              })}
            </div>
            <div
              ref={gridRef}
              className="stage1-grid"
              style={{
                gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridSize}, minmax(0, 1fr))`,
              }}
              onMouseLeave={() => {
                if (isDraggingRef.current) onPointerUp();
              }}
              onTouchMove={onGridTouchMove}
            >
              {puzzle.grid.map((row, r) =>
                row.map((letter, c) => {
                  const key = cellKey(r, c);
                  const isActive = activeSet.has(key);
                  return (
                    <div
                      key={key}
                      className={`stage1-cell${isActive ? " stage1-cell--active" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onPointerDown(r, c);
                      }}
                      onMouseEnter={() => onPointerEnter(r, c)}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        onPointerDown(r, c);
                      }}
                      data-row={r}
                      data-col={c}
                    >
                      {letter}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="stage1-slots">
            {STAGE1_DECORS.map((item) => (
              <input
                key={item.word}
                type="text"
                readOnly
                className="stage1-slot"
                value={foundWords.has(item.word) ? item.word : ""}
              />
            ))}
          </div>
        </div>

        <div className="stage1-right">
          <div className="stage1-office-wrap">
            <img src={officeImg} alt="" className="stage1-office" />
            <div className="stage1-office-parent">
              {STAGE1_DECORS.map((item) => (
                <div
                  key={item.word}
                  className={`stage1-child ${item.overlayClass}${
                    foundWords.has(item.word) ? " is-visible" : ""
                  }`}
                >
                  <img src={item.src} alt="" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="stage1-submit-wrap">
        <button
          type="button"
          className="stage1-submit"
          disabled={submitting || foundWords.size < MIN_WORDS_TO_ADVANCE}
          onClick={() => finishStage(false)}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
